/**
 * DviStageGroup - Stage 2: Finding group management
 * 
 * Auto-group or manually organize media into finding groups.
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Layers, 
  Loader2, 
  Plus, 
  Camera,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FindingGroup } from '@/hooks/useDviFindingGroups';

interface DviStageGroupProps {
  findingGroups: FindingGroup[];
  ungroupedCount: number;
  onAutoGroup: () => void;
  isAutoGrouping: boolean;
  canApprove: boolean;
  isLoading: boolean;
}

const CONDITION_COLORS = {
  red: 'bg-red-500/10 text-red-600 border-red-200',
  yellow: 'bg-yellow-500/10 text-yellow-600 border-yellow-200',
  green: 'bg-green-500/10 text-green-600 border-green-200',
};

export function DviStageGroup({
  findingGroups,
  ungroupedCount,
  onAutoGroup,
  isAutoGrouping,
  canApprove,
  isLoading,
}: DviStageGroupProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const pendingGroups = findingGroups.filter((g) => g.status === 'pending');
  const analyzedGroups = findingGroups.filter((g) => g.status !== 'pending');

  return (
    <div className="space-y-4">
      {/* Ungrouped Media Alert */}
      {ungroupedCount > 0 && (
        <Card className="border-yellow-200 bg-yellow-50/30">
          <CardContent className="p-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <span className="text-sm text-yellow-700">
                {ungroupedCount} media item{ungroupedCount > 1 ? 's' : ''} not in any group
              </span>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={onAutoGroup}
              disabled={isAutoGrouping}
              className="gap-1.5"
            >
              {isAutoGrouping ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Layers className="h-3 w-3" />
              )}
              Auto-Group
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="secondary">
          {findingGroups.length} Group{findingGroups.length !== 1 ? 's' : ''}
        </Badge>
        {pendingGroups.length > 0 && (
          <Badge variant="outline" className="bg-muted">
            {pendingGroups.length} pending analysis
          </Badge>
        )}
        {analyzedGroups.length > 0 && (
          <Badge variant="outline" className="bg-green-500/10 text-green-600">
            {analyzedGroups.length} analyzed
          </Badge>
        )}
      </div>

      {/* Finding Groups List */}
      {findingGroups.length === 0 ? (
        <div className="text-center py-8 space-y-2">
          <Layers className="h-12 w-12 text-muted-foreground mx-auto" />
          <p className="text-sm text-muted-foreground">No finding groups yet</p>
          <p className="text-xs text-muted-foreground">
            Upload media and use Auto-Group to organize them
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {findingGroups.map((group) => (
            <Card 
              key={group.id}
              className={cn(
                'transition-colors',
                group.condition && CONDITION_COLORS[group.condition]
              )}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Camera className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium truncate">
                      {group.title || 'Untitled Group'}
                    </span>
                    {group.media_count && (
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {group.media_count} media
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {group.status === 'pending' ? (
                      <Badge variant="outline" className="bg-muted text-xs">
                        Pending
                      </Badge>
                    ) : group.condition ? (
                      <Badge 
                        variant="outline" 
                        className={cn('text-xs capitalize', CONDITION_COLORS[group.condition])}
                      >
                        {group.condition}
                      </Badge>
                    ) : null}
                    {group.confidence !== null && (
                      <span className="text-xs text-muted-foreground">
                        {Math.round(group.confidence * 100)}%
                      </span>
                    )}
                  </div>
                </div>
                {group.summary && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {group.summary}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
